import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppMode, Lang } from '../types';

interface SettingsState {
  lang: Lang;
  theme: string;
  isLight: boolean;
  userContext: string;
  mapContext: string;
  currentMode: AppMode;
  flashcardMode: boolean;
  showFlashcardIcons: boolean;

  setLang: (lang: Lang) => void;
  setTheme: (theme: string) => void;
  toggleTheme: () => void;
  setUserContext: (ctx: string) => void;
  setMapContext: (ctx: string) => void;
  setCurrentMode: (mode: AppMode) => void;
  setFlashcardMode: (on: boolean) => void;
  setShowFlashcardIcons: (on: boolean) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      lang: 'en',
      theme: 'mocha',
      isLight: false,
      userContext: '',
      mapContext: '',
      currentMode: 'brainstormer',
      flashcardMode: false,
      showFlashcardIcons: true,

      setLang: (lang) => set({ lang }),
      setTheme: (theme) => {
        document.documentElement.classList.toggle('latte', theme === 'latte');
        set({ theme, isLight: theme === 'latte' });
      },
      toggleTheme: () =>
        set((s) => {
          const next = s.theme === 'mocha' ? 'latte' : 'mocha';
          document.documentElement.classList.toggle('latte', next === 'latte');
          return { theme: next, isLight: next === 'latte' };
        }),
      setUserContext: (userContext) => set({ userContext }),
      setMapContext: (mapContext) => set({ mapContext }),
      setCurrentMode: (currentMode) => set({ currentMode }),
      setFlashcardMode: (flashcardMode) => set({ flashcardMode }),
      setShowFlashcardIcons: (showFlashcardIcons) => set({ showFlashcardIcons }),
    }),
    {
      name: 'zustand_settings',
      partialize: (state) => ({
        lang: state.lang,
        theme: state.theme,
        userContext: state.userContext,
      }),
      onRehydrateStorage: () => {
        return (state?: SettingsState) => {
          if (state?.theme) {
            document.documentElement.classList.toggle('latte', state.theme === 'latte');
          }
        };
      },
      // Migrate from legacy brainstormer_settings if zustand_settings doesn't exist yet
      storage: {
        getItem: (name) => {
          const stored = localStorage.getItem(name);
          if (stored) return JSON.parse(stored);
          // Migrate from legacy format
          try {
            const legacy = localStorage.getItem('brainstormer_settings');
            if (legacy) {
              const parsed = JSON.parse(legacy);
              const migrated = {
                state: {
                  lang: parsed.lang ?? 'en',
                  theme: parsed.theme ?? 'mocha',
                  userContext: parsed.userContext ?? '',
                },
                version: 0,
              };
              return migrated;
            }
          } catch { /* ignore */ }
          return null;
        },
        setItem: (name, value) => localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
